import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useContext,
} from 'react';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import { Input } from 'grab-ui';
import leftArrow from 'public/icons/left-arrow-black.svg';
import _debounce from 'lodash/debounce';
import OfferTermCondition from './offer-term-condition';
import OfferListComponent from './offer-list-component';
import OfferListFooter from './offer-list-footer';
import OverlayBody from '../overlay-body';
import styles from './styles.less';
import OfferPanelContext from './offer-panel-context';

const PromoCodeInputComponent = ({
  delivery,
  offerList,
  selectedOffer,
  fetchingPromo,
  onClose,
  onCloseOverlay,
  offerConfig,
  onUseOffer,
  onRemoveOffer,
  onSearchTextChange,
}) => {
  const selectedOfferID = _get(selectedOffer, 'offerID', null);
  const [showOfferDetail, setShowOfferDetail] = useState(false);
  const [promoKeyword, setPromoKeyword] = useState(undefined);
  const [offerTC, setOfferTC] = useState(null);

  const {
    handleScribeEnterCodeDetailsTypeCode,
    handleScribeEnterCodeDetailsShowErrorResult,
  } = useContext(OfferPanelContext);

  const onClosePromoCodeInput = useCallback(
    () => {
      onClose();
    },
    [onClose],
  );

  useEffect(() => {
    setTimeout(() => {
      const elem = document.getElementById('promoCodeInput');
      if (elem) {
        elem.focus();
      }
    }, 200);
  }, []);

  const onCloseOfferDetail = useCallback(
    () => {
      setShowOfferDetail(false);
      setOfferTC(null);
    },
    [setShowOfferDetail, setOfferTC],
  );

  const handleViewOfferDetail = useCallback(
    offerID => {
      const chooseOffer = offerList.filter(
        offer => offer.offerID === offerID,
      )[0];

      if (chooseOffer) {
        setOfferTC(chooseOffer);
        setShowOfferDetail(true);
      }
    },
    [offerList],
  );

  const handleOnUseOffer = useCallback(
    offerID => () => {
      onCloseOfferDetail();
      onUseOffer(offerID);
    },
    [onCloseOfferDetail, onUseOffer],
  );

  const handleOnRemoveOffer = useCallback(
    () => {
      onCloseOfferDetail();
      onRemoveOffer();
    },
    [onCloseOfferDetail, onRemoveOffer],
  );

  const onSearchTextChangeFunc = useCallback(
    _debounce(promoCode => {
      onSearchTextChange(promoCode);
      handleScribeEnterCodeDetailsTypeCode({ promoCode });

      // For scribe
      setPromoKeyword(promoCode);
    }, 300),

    [delivery, setPromoKeyword],
  );

  const onTypeInPromoCode = useCallback(
    e => onSearchTextChangeFunc(e.target.value),
    [],
  );

  const isNoResultFound = useMemo(
    () => {
      if (fetchingPromo) {
        return false;
      }
      const element = document.getElementById('promoCodeInput');
      if (element) {
        const val = element.value;
        const isNoResult = val && (!offerList || offerList.length === 0);
        handleScribeEnterCodeDetailsShowErrorResult({
          promoKeyword,
          isNoResult,
        });
        return isNoResult;
      }
      return false;
    },
    [offerList],
  );

  if (showOfferDetail) {
    return (
      <OfferTermCondition
        delivery={delivery}
        offer={offerTC}
        selectedOfferID={selectedOfferID}
        onClose={onCloseOfferDetail}
        onSelectOffer={handleOnUseOffer(offerTC.offerID)}
        onRemoveOffer={handleOnRemoveOffer}
      />
    );
  }

  return (
    <OverlayBody
      title={I18n.t(
        'phrase.offers.mktplace_offers_bottom_sheet_body_save_using_codes_promo_codes',
      )}
      showHeader
      showTitle
      onClose={onClosePromoCodeInput}
      customHeaderIcon={leftArrow}
      showFooter
      showConfirm={false}
      footer={
        <OfferListFooter
          onClose={onCloseOverlay}
          offerList={offerList}
          selectedOffer={selectedOffer}
          offerConfig={offerConfig}
          isPromoSearch
        />
      }
      classnames={{
        footer: styles.footer,
      }}
    >
      <div className={styles.promoListContainer}>
        <Input
          id="promoCodeInput"
          placeholder={I18n.t('phrase.offers.MKTPLACE_OFFERS_ENTER_PROMO_CODE')}
          onChange={onTypeInPromoCode}
        />
        {isNoResultFound ? (
          <div className={styles.isNoResultFound}>
            {I18n.t(
              'phrase.offers.mktplace_offers_bottom_sheet_body_error_promo_codes',
            )}
          </div>
        ) : (
          <OfferListComponent
            onViewOfferDetail={handleViewOfferDetail}
            offerList={offerList}
            selectedOffer={selectedOffer}
            offerConfig={offerConfig}
            fetchingPromo={fetchingPromo}
            isPromoSearch
          />
        )}
      </div>
    </OverlayBody>
  );
};

PromoCodeInputComponent.defaultProps = {
  onClose: undefined,
  offerConfig: undefined,
  selectedOffer: undefined,
};

PromoCodeInputComponent.propTypes = {
  delivery: PropTypes.shape({}).isRequired,
  onClose: PropTypes.func,
  onCloseOverlay: PropTypes.func.isRequired,
  onUseOffer: PropTypes.func.isRequired,
  onRemoveOffer: PropTypes.func.isRequired,
  offerList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  offerConfig: PropTypes.shape({}),
  selectedOffer: PropTypes.shape({}),
  fetchingPromo: PropTypes.bool.isRequired,
  onSearchTextChange: PropTypes.func.isRequired,
};

export default PromoCodeInputComponent;
